import { type FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import LiveTvOutlinedIcon from "@mui/icons-material/LiveTvOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { DialogHeader, ErrorMessage } from "@/shared/components";
import {
  AssistantDialogBlock,
  initials,
  OriginalEmailDialog,
  RequestSummaryCard,
  ThreadComposer,
  ThreadFeed,
  isNearBottom,
  type ComposerSubmit,
} from "@/shared/components/SupportThread";
import { interpolationFormat } from "@/partner/shared/lib";
import { skipHiddenPagePoll } from "@/shared/utils/skipHiddenPagePoll";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { formatAmountObject } from "@/partner/features/products";
import {
  REQUEST_TYPE_LABEL_KEY,
  SupportRequestStatusChip,
  useAssignSupportRequestMutation,
  useReplySupportRequestMutation,
  useSetSupportRequestStatusMutation,
  useSupportRequestAssigneesQuery,
  useSupportRequestMessageOriginalLazyQuery,
  useSupportRequestQuery,
} from "@/partner/entities/assistant";
import { SupportRequestStatusType } from "@/types/graphql/partner.generated";
import {
  REQUEST_CARD_POLL_MS,
  REQUEST_REFETCH_QUERIES,
  displayName,
  requestErrorKey,
  takeoverHint,
  toDialogMessages,
  toThreadMessages,
} from "../model/thread";
import { viewerPaymentsHref } from "../model/links";
import { SupportRequestInfoPanel } from "./SupportRequestInfoPanel";

interface Props {
  open: boolean;
  requestId: string | null;
  onClose: () => void;
}

const REFETCH = [...REQUEST_REFETCH_QUERIES];
/** Ширина колонки переписки: строки читаются, не растягиваясь на весь экран. */
const COLUMN_WIDTH = 920;

/**
 * Карточка обращения как в почтовых клиентах поддержки (спека 3.1): диалог
 * с помощником, лента переписки, закреплённое поле ответа с внутренней
 * заметкой и «и решить», ответственный и кнопка статуса в шапке.
 */
export const SupportRequestDialog: FC<Props> = ({
  open,
  requestId,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useDeviceBreakpoint("md");
  const endRef = useRef<HTMLDivElement | null>(null);
  // Лента следует за новыми сообщениями, только пока сотрудник у её конца:
  // прокрутил вверх и читает, опрос карточки его не сдёргивает.
  const stickToEndRef = useRef(true);
  const [originalId, setOriginalId] = useState<string | null>(null);

  const { data, loading, error } = useSupportRequestQuery({
    variables: { id: requestId ?? "" },
    skip: !requestId || !open,
    fetchPolicy: "cache-and-network",
    // Пока карточка открыта: новый ответ зрителя письмом. В скрытой вкладке
    // тик пропускается, закрытая карточка (skip) не опрашивает.
    pollInterval: REQUEST_CARD_POLL_MS,
    skipPollAttempt: skipHiddenPagePoll,
  });
  const request = data?.supportRequest ?? null;
  const assignees = useSupportRequestAssigneesQuery({ skip: !open });
  const [loadOriginal, original] = useSupportRequestMessageOriginalLazyQuery();

  const onError = (e: Parameters<typeof requestErrorKey>[0]) =>
    enqueueSnackbar(t(requestErrorKey(e)), { variant: "error" });
  const [reply, { loading: replying }] = useReplySupportRequestMutation({
    refetchQueries: REFETCH,
    onError,
  });
  const [assign, { loading: assigning }] = useAssignSupportRequestMutation({
    refetchQueries: REFETCH,
    onError,
  });
  const [setStatus, { loading: settingStatus }] =
    useSetSupportRequestStatusMutation({ refetchQueries: REFETCH, onError });

  const messages = useMemo(
    () => toThreadMessages(request?.messages ?? []),
    [request?.messages]
  );
  const dialog = useMemo(
    () => toDialogMessages(request?.assistantMessages ?? []),
    [request?.assistantMessages]
  );

  useEffect(() => {
    if (stickToEndRef.current) {
      endRef.current?.scrollIntoView({ block: "end" });
    }
  }, [messages.length]);

  // Другое обращение открывается с конца ленты.
  useEffect(() => {
    stickToEndRef.current = true;
  }, [requestId]);

  const formatTime = (iso: string) =>
    interpolationFormat(iso, "short", i18n.language);
  const empty = t("common.emptyDash");
  const resolved = `${request?.status ?? ""}` === "CLOSED";
  const teamWrote = messages.some((m) => m.author === "STAFF" && !m.internal);
  const hint = request && !teamWrote ? takeoverHint(request) : null;
  const placeholderHint = hint
    ? hint.summary
      ? t("requests.card.takeoverHint", {
          name: hint.name,
          summary: hint.summary,
        })
      : t("requests.card.takeoverHintNoSummary", { name: hint.name })
    : null;

  const handleSubmit = async ({ text, resolve, internal }: ComposerSubmit) => {
    if (!requestId) return false;
    const result = await reply({
      variables: { id: requestId, input: { text, resolve, internal } },
    });
    if (result.errors?.length || !result.data) return false;
    // Свой ответ сотрудник видит сразу, даже если читал ленту выше.
    stickToEndRef.current = true;
    enqueueSnackbar(
      t(
        internal
          ? "requests.card.noteSaved"
          : resolve
            ? "requests.card.resolvedSnackbar"
            : "requests.card.sent"
      ),
      { variant: "success" }
    );
    return true;
  };

  const handleStatus = () => {
    if (!requestId) return;
    void setStatus({
      variables: {
        id: requestId,
        input: {
          status: resolved
            ? SupportRequestStatusType.OpenType
            : SupportRequestStatusType.ClosedType,
        },
      },
      onCompleted: () =>
        enqueueSnackbar(
          t(
            resolved
              ? "requests.card.reopenedSnackbar"
              : "requests.card.resolvedSnackbar"
          ),
          { variant: "success" }
        ),
    });
  };

  const handleAssign = (assigneeId: string) => {
    if (!requestId) return;
    void assign({
      variables: { id: requestId, assigneeId: assigneeId || null },
      onCompleted: () =>
        enqueueSnackbar(t("requests.card.assignedSnackbar"), {
          variant: "success",
        }),
    });
  };

  const title = request
    ? t("requests.card.headerTitle", {
        number: request.number ?? "",
        subject: request.subject,
      })
    : t("requests.dialogTitle");

  const viewerEmail = request?.user?.email ?? "";
  const viewerName = displayName(request?.user);
  const assigneeOptions = assignees.data?.supportRequestAssignees ?? [];
  const assigneeLabel = (id: string) => {
    const person =
      assigneeOptions.find((u) => u.id === id) ??
      (request?.assignee?.id === id ? request.assignee : null);
    return displayName(person) ?? id;
  };

  const assigneePicker = request ? (
    <TextField
      key="assignee"
      select
      size="small"
      value={request.assignee?.id ?? ""}
      onChange={(e) => handleAssign(e.target.value)}
      disabled={assigning}
      sx={{ minWidth: 220, flex: { xs: 1, md: "none" } }}
      slotProps={{
        select: {
          displayEmpty: true,
          inputProps: { "aria-label": t("requests.card.assignee") },
          renderValue: (value) => {
            const id = String(value ?? "");
            const name = id ? assigneeLabel(id) : null;
            return (
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: 12,
                    ...(name && {
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                    }),
                  }}
                >
                  {name ? (
                    initials(name)
                  ) : (
                    <PersonOutlineIcon sx={{ fontSize: 16 }} />
                  )}
                </Avatar>
                <Typography
                  variant="body2"
                  noWrap
                  color={name ? "text.primary" : "text.secondary"}
                >
                  {name ?? t("requests.card.noAssignee")}
                </Typography>
              </Stack>
            );
          },
        },
      }}
    >
      <MenuItem value="">
        <ListItemAvatar sx={{ minWidth: 40 }}>
          <Avatar sx={{ width: 28, height: 28 }}>
            <PersonOutlineIcon sx={{ fontSize: 18 }} />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={t("requests.card.noAssignee")}
          slotProps={{ primary: { color: "text.secondary" } }}
        />
      </MenuItem>
      {assigneeOptions.map((u) => {
        const name = displayName(u) ?? u.id;
        return (
          <MenuItem key={u.id} value={u.id}>
            <ListItemAvatar sx={{ minWidth: 40 }}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: 13,
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                }}
              >
                {initials(name)}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={name}
              secondary={u.email && u.email !== name ? u.email : undefined}
            />
          </MenuItem>
        );
      })}
    </TextField>
  ) : null;

  const statusButton = request ? (
    <Button
      key="status"
      variant={resolved ? "outlined" : "contained"}
      disableElevation
      color={resolved ? "inherit" : "success"}
      startIcon={resolved ? <ReplayIcon /> : <CheckRoundedIcon />}
      disabled={settingStatus}
      onClick={handleStatus}
    >
      {resolved ? t("requests.card.reopen") : t("requests.card.resolve")}
    </Button>
  ) : null;

  const headerChips = request ? (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      <SupportRequestStatusChip status={request.status} />
      <Chip
        size="small"
        variant="outlined"
        icon={<CategoryOutlinedIcon />}
        label={t(REQUEST_TYPE_LABEL_KEY[request.type])}
      />
      {request.platform?.title && (
        <Chip
          size="small"
          variant="outlined"
          icon={<LiveTvOutlinedIcon />}
          label={request.platform.title}
        />
      )}
    </Stack>
  ) : undefined;

  // Пока переписка с командой пуста, диалог с помощником раскрыт: это всё,
  // что известно. Как только есть ответы, он свёрнут в предысторию.
  const hasThread = messages.some((m) => m.author !== "SYSTEM");

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogHeader
        title={title}
        singleLineTitle
        titleTooltip
        subtitle={headerChips}
        loading={loading || assigning || settingStatus}
        onClose={onClose}
        actions={request ? [assigneePicker, statusButton] : undefined}
      />
      {error && <ErrorMessage error={error} />}
      {!request && loading && <CardSkeleton />}
      {request && (
        <Stack
          direction={{ xs: "column", md: "row" }}
          sx={{ flex: 1, minHeight: 0 }}
        >
          <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: { xs: 1, md: 3 },
                pt: { xs: 1, md: 3 },
                pb: 2,
              }}
              onScroll={(e) => {
                stickToEndRef.current = isNearBottom(e.currentTarget);
              }}
            >
              <Stack spacing={2} sx={{ maxWidth: COLUMN_WIDTH, mx: "auto" }}>
                {isMobile && <SupportRequestInfoPanel request={request} />}
                <RequestSummaryCard
                  viewerName={viewerName}
                  summary={{
                    typeLabel: t(REQUEST_TYPE_LABEL_KEY[request.type]),
                    message: request.message,
                    details: request.details ?? null,
                    paymentLabel: request.payment
                      ? formatAmountObject(request.payment.amount, empty)
                      : null,
                    paymentHref:
                      request.payment && viewerEmail
                        ? viewerPaymentsHref(viewerEmail)
                        : null,
                  }}
                />
                <AssistantDialogBlock
                  key={request.id}
                  messages={dialog}
                  formatTime={formatTime}
                  viewerName={viewerName}
                  defaultExpanded={!hasThread}
                />
                <ThreadFeed
                  messages={messages}
                  formatTime={formatTime}
                  viewerName={viewerName}
                  onShowOriginal={(id) => {
                    setOriginalId(id);
                    void loadOriginal({ variables: { id } });
                  }}
                />
                <div ref={endRef} />
              </Stack>
            </Box>
            <Box sx={{ px: { xs: 1, md: 3 }, pb: { xs: 1, md: 2 } }}>
              <Box sx={{ maxWidth: COLUMN_WIDTH, mx: "auto" }}>
                <ThreadComposer
                  placeholderHint={placeholderHint}
                  sending={replying}
                  canResolve={!resolved}
                  onSubmit={handleSubmit}
                />
              </Box>
            </Box>
          </Stack>
          {!isMobile && (
            <Box
              sx={{
                width: 340,
                flexShrink: 0,
                overflowY: "auto",
                pt: 3,
                pr: 3,
                pb: 2,
              }}
            >
              <SupportRequestInfoPanel request={request} />
            </Box>
          )}
        </Stack>
      )}
      <OriginalEmailDialog
        open={Boolean(originalId)}
        html={original.data?.supportRequestMessageOriginal ?? null}
        loading={original.loading}
        onClose={() => setOriginalId(null)}
      />
    </Dialog>
  );
};

/** Первая загрузка карточки: очертания колонок вместо крутилки. */
const CardSkeleton: FC = () => (
  <Stack
    direction="row"
    spacing={3}
    sx={{ px: { xs: 1, md: 3 }, pt: { xs: 1, md: 3 } }}
    data-testid="request-card-skeleton"
  >
    <Stack spacing={2} sx={{ flex: 1, maxWidth: COLUMN_WIDTH, mx: "auto" }}>
      <Skeleton variant="rounded" height={160} />
      <Skeleton variant="rounded" height={72} />
      <Skeleton variant="rounded" height={280} />
    </Stack>
    <Skeleton
      variant="rounded"
      width={340}
      height={420}
      sx={{ display: { xs: "none", md: "block" }, flexShrink: 0 }}
    />
  </Stack>
);
